import { FormEvent, useState } from 'react';
import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
  SupportButton,
  WarningPanel,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';

/** Mirrors the backend `QueryResponse` (packages/backend/src/rag/types.ts). */
interface RetrievalHit {
  repo: string;
  page: string;
  url: string;
  content: string;
  score: number;
}
interface QueryResponse {
  query: string;
  hits: RetrievalHit[];
  answer?: string;
}

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  hitHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  snippet: {
    whiteSpace: 'pre-wrap',
    color: theme.palette.text.secondary,
  },
  empty: {
    color: theme.palette.text.secondary,
  },
}));

export const AskDocsPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<QueryResponse>();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('rag');
      const res = await fetchApi.fetch(`${baseUrl}/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q }),
      });
      if (!res.ok) {
        throw new Error(`La requête a échoué (${res.status} ${res.statusText})`);
      }
      setResult((await res.json()) as QueryResponse);
    } catch (err) {
      setError((err as Error).message);
      setResult(undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header
        title="Ask the docs"
        subtitle="Recherche sémantique sur toute la doc du homelab (TechDocs)"
      />
      <Content>
        <ContentHeader title="Interroger la documentation">
          <SupportButton>
            Le RAG cherche localement (pgvector) les passages de doc les plus
            proches de votre question, puis renvoie les sources citées. La
            génération d'une réponse rédigée est désactivée par défaut.
          </SupportButton>
        </ContentHeader>

        <form className={classes.form} onSubmit={onSubmit}>
          <TextField
            fullWidth
            variant="outlined"
            label="Votre question"
            placeholder="ex: comment est configuré le forward-auth Authelia ?"
            value={query}
            onChange={e => setQuery(e.target.value)}
            disabled={loading}
          />
          <Box pt={0.5}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !query.trim()}
            >
              Chercher
            </Button>
          </Box>
        </form>

        {loading && <Progress />}

        {error && (
          <Box mb={2}>
            <WarningPanel title="Erreur" message={error} />
          </Box>
        )}

        {result?.answer && (
          <Box mb={2}>
            <InfoCard title="Réponse générée">
              <Typography variant="body1" className={classes.snippet}>
                {result.answer}
              </Typography>
            </InfoCard>
          </Box>
        )}

        {result && result.hits.length === 0 && (
          <Typography variant="body1" className={classes.empty}>
            Aucun passage de documentation ne correspond à cette question.
          </Typography>
        )}

        {result && result.hits.length > 0 && (
          <Grid container spacing={2}>
            {result.hits.map((hit, i) => (
              <Grid item xs={12} key={`${hit.url}-${i}`}>
                <InfoCard>
                  <div className={classes.hitHeader}>
                    <Link to={hit.url} target="_blank">
                      <Typography variant="h6">{hit.page}</Typography>
                    </Link>
                    <Chip size="small" label={hit.repo} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`score ${hit.score.toFixed(3)}`}
                    />
                  </div>
                  <Typography variant="body2" className={classes.snippet}>
                    {hit.content}
                  </Typography>
                </InfoCard>
              </Grid>
            ))}
          </Grid>
        )}
      </Content>
    </Page>
  );
};
